import { useEffect, useMemo, useState } from "react";
import { useLocation } from "@tanstack/react-router";

import { apiClient } from "../lib/api";
import { pathnameToPageKey } from "../components/announcement/routeMap";
import type { AnnouncementSeverity } from "../components/announcement/severity";

export interface ActiveAnnouncement {
  id: number;
  mode: "banner" | "page";
  severity: AnnouncementSeverity;
  title: string;
  message: string;
  dismissible: boolean;
  target_pages: string[];
  updated_at: string;
}

interface ActiveAnnouncementsResponse {
  announcements: ActiveAnnouncement[];
}

const POLL_INTERVAL_MS = 60_000;

function dismissKey(a: ActiveAnnouncement): string {
  return `charti.announcement.dismissed.${a.id}.${a.updated_at}`;
}

export function isDismissed(a: ActiveAnnouncement): boolean {
  try {
    return window.sessionStorage.getItem(dismissKey(a)) === "1";
  } catch {
    return false;
  }
}

export function markDismissed(a: ActiveAnnouncement): void {
  try {
    window.sessionStorage.setItem(dismissKey(a), "1");
  } catch {
    /* sessionStorage unavailable — ignore */
  }
}

export function useAnnouncement() {
  const [candidates, setCandidates] = useState<ActiveAnnouncement[]>([]);
  const [loading, setLoading] = useState(true);
  const [dismissTick, setDismissTick] = useState(0);
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;

    const fetchActive = async () => {
      try {
        const res = await apiClient.get<ActiveAnnouncementsResponse>(
          "/api/announcements/active"
        );
        if (!cancelled) {
          setCandidates(res.data.announcements ?? []);
        }
      } catch {
        if (!cancelled) setCandidates([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchActive();
    const interval = window.setInterval(fetchActive, POLL_INTERVAL_MS);
    const onFocus = () => fetchActive();
    window.addEventListener("focus", onFocus);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const active = useMemo<ActiveAnnouncement | null>(() => {
    if (candidates.length === 0) return null;
    const currentKey = pathnameToPageKey(location.pathname);

    // 1. Any blocking page wins — ignores target_pages.
    const blocking = candidates.find((c) => c.mode === "page");
    if (blocking) return blocking;

    // 2. First banner whose target_pages matches current route
    //    (empty target_pages means "show on all pages").
    for (const c of candidates) {
      if (c.mode !== "banner") continue;
      if (c.dismissible && isDismissed(c)) continue;
      if (
        c.target_pages.length === 0 ||
        (currentKey && c.target_pages.includes(currentKey))
      ) {
        return c;
      }
    }
    return null;
    // dismissTick forces re-eval after a dismiss click
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidates, location.pathname, dismissTick]);

  const dismiss = (a: ActiveAnnouncement) => {
    markDismissed(a);
    setDismissTick((n) => n + 1);
  };

  return { active, loading, dismiss };
}
